/* eslint-disable import/no-named-as-default-member */
import { CheckIcon, Divider, GKButton, Grid2, Typography } from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import DashboardLayout from "layouts/DashboardLayout";
import { map } from "lodash";
import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { GET_RISK_PROFILE } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import useDocumentTitle from "utils/useDocumentTitle";

const Questionnaire = () => {
  useDocumentTitle("Risk Profile Question");

  const navigate = useNavigate();

  const { id, type } = useParams();

  const [questionAnswers, setQuestionAnswers] = useState([]);

  const [questionValidation, setQuestionValidation] = useState<any>({});

  // ===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*
  const { data } = useFetchQuery({
    key: ["RISK_PROFILE_LIST"],
    route: GET_RISK_PROFILE,
  });

  // ===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*
  const { mutate: riskProfileResponse } = useMutation({
    mutationKey: ["RISK_PROFILE_QUESTION"],
    mutationFn: (data: any) => axiosInstance.post(data.url, data.data),
    onSuccess: () => {
      navigate(-1);
    },
  });

  // ===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*
  const handleOptionChange = (questionId: any, optionItem: any) => {
    const dataToSubmit = {
      question: questionId,
      option: optionItem.id,
      optionPoints: optionItem.optionPoints,
    };

    const isExistQuestion = questionAnswers
      .map((item) => item.question)
      .includes(dataToSubmit.question);

    if (isExistQuestion) {
      const indexToRemove = questionAnswers.findIndex(
        (item) => item.question === dataToSubmit.question,
      );

      if (indexToRemove !== -1) {
        questionAnswers.splice(indexToRemove, 1);
      }
    }

    setQuestionAnswers((prevValue) => [...prevValue, dataToSubmit]);

    const updatedValidation = { ...questionValidation, [questionId]: true };
    setQuestionValidation(updatedValidation);
  };

  // ===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*
  const isQuestionAnswered = (questionId: any) => {
    return questionValidation[questionId] || false;
  };

  // ===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*
  const handleSubmit = async () => {
    const allQuestionsAnswered = data?.data?.data?.data?.riskQuestions?.every(
      (dataItem: any) => isQuestionAnswered(dataItem.id),
    );

    if (!allQuestionsAnswered) {
      toast.error("Please answer all questions before submitting.");
    }

    if (type === "lead") {
      riskProfileResponse({
        url: `/lead-riskprofileresponse/${id}/`,
        data: {
          lead: id,
          response: questionAnswers,
        },
      });
    } else {
      riskProfileResponse({
        url: `/riskprofileresponse/${id}/`,
        data: {
          client: id,
          response: questionAnswers,
        },
      });
    }
  };

  return (
    <DashboardLayout
      title={type === "lead" ? "Lead Risk Profile" : "Client Risk Profile"}
    >
      <Grid2 container spacing={1}>
        <Grid2 size={12}>
          {data?.data?.data?.data?.riskQuestions?.map(
            (dataItem: any, index: number) => {
              return (
                <Grid2 container key={dataItem?.id}>
                  <Grid2 size={12}>
                    <Typography
                      fontSize={14}
                      fontWeight={600}
                      color={"var(--grey-900)"}
                    >
                      <span>{index + 1}. </span>
                      {dataItem.questionText}
                    </Typography>
                  </Grid2>

                  <Grid2 size={6}>
                    {map(dataItem?.options, (item) => {
                      const isSelected = questionAnswers.find(
                        (data) =>
                          data.question === dataItem.id &&
                          data.option === item.id,
                      );

                      return (
                        <Typography
                          fontSize={13}
                          fontWeight={500}
                          color="var(--grey-900)"
                          key={item.id}
                          p={"14px 8px"}
                          display={"flex"}
                          alignItems={"center"}
                          gap={1}
                          sx={{ cursor: "pointer" }}
                          borderRadius={1}
                          bgcolor={isSelected ? "var(--primary-50)" : undefined}
                          onClick={() =>
                            handleOptionChange(dataItem.id, { ...item })
                          }
                        >
                          {isSelected && <CheckIcon />}
                          {item?.option}
                        </Typography>
                      );
                    })}
                  </Grid2>

                  <Grid2 size={12} py={2}>
                    <Divider />
                  </Grid2>
                </Grid2>
              );
            },
          )}
        </Grid2>

        <Grid2
          size={12}
          display={"flex"}
          justifyContent={"flex-end"}
          alignItems={"center"}
          gap={2}
        >
          <GKButton
            variant="contained"
            size="medium"
            onClick={handleSubmit}
            startIcon={<CheckIcon />}
          >
            Save & Continue
          </GKButton>
        </Grid2>
      </Grid2>
    </DashboardLayout>
  );
};

export default Questionnaire;
